import React, { useEffect, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";

import StatsCard from "./stats-card";
import Loader from "../../components/loader";
import MainButton from "../../components/main-button";
import NotificacionCarousel from "../../components/notification-carousel";
import { HomeContainer } from "./home-style";
import { images } from "../../assets";

import { withGeneral } from "../../context/mixin/with-general";
import { TABPARAMNAME } from "../../constants/clientsDashboard";

import StatsServices from "../../services/StatsServices";
import CommunicationsServices from "../../services/CommunicationsServices";
import { PrivilegeCode } from "../../services/PrivilegesServices-dto";
import { getPrivilege } from "../../utils/privileges";
import { NotificationType } from "../communications/helpers";
import { IUser } from "../../models/user-model";
import { IGetStatsResponseData } from "../../services/StatsServices.dto";

interface HomeProps extends RouteComponentProps {
  setLoading: Function;
  user: IUser;
}

const HomeLayout = ({ user, history }: HomeProps) => {
  const defaultStats = {
    expiredSimulations: NaN,
    hirings: NaN,
    simulations: NaN,
    upcomingRenewals: NaN,
    EIACFilesCount: NaN,
    unpaidReceipts: NaN,
  };
  const [loading, setLoading] = useState<boolean>(false);
  const [stats, setStats] = useState<IGetStatsResponseData>(defaultStats);
  const [notifications, setNotifications] = useState<NotificationType[]>([]);

  useEffect(() => {
    getStats();
    getNotifications();
  }, []);

  const getStats = async () => {
    const response = await StatsServices.getStats({
      loader: true,
      setLoading,
    });
    if (response.ok) {
      setStats(response.data);
    } else {
      setStats(defaultStats);
    }
  };

  const getNotifications = async () => {
    const response = await CommunicationsServices.getHomeNotifications(user);
    if (response.ok) {
      setNotifications(response.data);
    }
  };

  const getStatValue = (card: keyof IGetStatsResponseData): number | string => {
    if (Number.isNaN(stats[card])) {
      return "";
    }
    return stats[card];
  };

  const privileges = {
    btnSimula: getPrivilege(user, PrivilegeCode.BTN_SIMULA),
    btnSimula2: getPrivilege(user, PrivilegeCode.BTN_SIMULA2),
    btnContrata: getPrivilege(user, PrivilegeCode.BTN_CONTRATA),
    btnExpiradas: getPrivilege(user, PrivilegeCode.BTN_EXPIRADAS),
    btnRenovacion: getPrivilege(user, PrivilegeCode.BTN_RENOVACIONES),
    btnEiacFiles: getPrivilege(user, PrivilegeCode.BTN_EIAC),
    btnUnpaidInvoices: getPrivilege(user, PrivilegeCode.BTN_IMPAGADOS),
    pestanaRenovacion: getPrivilege(user, PrivilegeCode.CM_RENOVACIONES),
    pestanaImpagados: getPrivilege(user, PrivilegeCode.CM_IMPAGADOS),
    cuadroMando: getPrivilege(user, PrivilegeCode.CUADRO_MANDO),
  };

  return (
    <HomeContainer>
      {loading && <Loader />}
      {notifications.length > 0 && (
        <div className="notifications-container">
          <NotificacionCarousel notifications={notifications} />
        </div>
      )}

      <div className="home-hero-intro-container">
        {privileges.btnSimula && (
          <div className="home-hero-intro-item text">
            <div className="home-hero-intro-body">
              <p>¿Quieres hacer una nueva simulación?</p>
            </div>
            <div className="home-hero-intro-btn">
              <MainButton text="Simular" onClick={() => history.push("/simulacion/nueva")} />
            </div>
          </div>
        )}

        <div className="home-hiring-data-container">
          <div className="home-hiring-data-top-container">
            <div className="home-hero-intro-body">
              <p>Contratación</p>
            </div>
          </div>

          <div>
            <div className="home-hiring-data-middle-container">
              <div className="home-hiring-data-amount">
                <p className="home-hiring-data-amount-hired">{getStatValue("hirings")}</p>
              </div>
              <div className="home-hiring-data-info">
                <p>
                  Contrataciones desde
                  <br /> simulación
                </p>
              </div>
            </div>

            {privileges.btnContrata && (
              <div className="home-hiring-data-btn-container">
                <MainButton
                  text="Contratar"
                  onClick={() => history.push("/contratacion/pendiente?new=true")}
                />
              </div>
            )}
          </div>
        </div>

        <div className="pic">
          <img src={images.newHeroImage} alt="" />
        </div>
      </div>

      <section className="home-stats">
        <StatsCard
          title="Simulación"
          stats={getStatValue("simulations")}
          privilege={privileges.btnSimula2}
          actionText="Simular"
          onClick={() => history.push("/simulacion/nueva")}
        >
          <p>
            Simulaciones en los
            <br />
            últimos 30 días
          </p>
        </StatsCard>

        <StatsCard
          title=""
          stats={getStatValue("expiredSimulations")}
          privilege={privileges.btnExpiradas}
          actionText="Ver"
          onClick={() => history.push("/simulacion/expirada")}
        >
          <p>
            Simulaciones
            <br />
            expiradas
          </p>
        </StatsCard>

        <StatsCard
          title="Ficheros EIAC"
          stats={getStatValue("EIACFilesCount")}
          actionText="Ver"
          privilege={privileges.btnEiacFiles}
          onClick={() => history.push(`/cuadro-de-mando?tab=${TABPARAMNAME.FICHEROS_EIAC}`)}
        >
          <p>
            Ficheros EIAC
            <br />
            generados
          </p>
        </StatsCard>

        <StatsCard
          title="Renovación"
          stats={getStatValue("upcomingRenewals")}
          privilege={
            privileges.btnRenovacion && privileges.cuadroMando && privileges.pestanaRenovacion
          }
          actionText="Ver"
          onClick={() => history.push(`/cuadro-de-mando?tab=${TABPARAMNAME.RENOVACIONES}`)}
        >
          <p>
            Pólizas próximas a<br />
            renovar
          </p>
        </StatsCard>

        <StatsCard
          title="Impagados"
          stats={getStatValue("unpaidReceipts")}
          privilege={
            privileges.btnUnpaidInvoices && privileges.cuadroMando && privileges.pestanaImpagados
          }
          actionText="Ver"
          onClick={() => history.push(`/cuadro-de-mando?tab=${TABPARAMNAME.IMPAGADOS}`)}
        >
          <p>
            Recibos
            <br />
            impagados
          </p>
        </StatsCard>
      </section>
    </HomeContainer>
  );
};

export default withGeneral(withRouter(HomeLayout));
